import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Paper, Alert,
} from '@mui/material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'details', title: 'Schedule Details' },
  { id: 'review', title: 'Review & Submit' },
];

interface FormValues {
  tutorId: string;
  agreementId: string;
}

const INITIAL_VALUES: FormValues = {
  tutorId: '',
  agreementId: '',
};

const stepDescriptions: Record<string, string> = {
  details: "Link this schedule to an existing tutoring agreement and assign a tutor.",
  review: "Review the schedule before creating. Sessions will be generated from this pattern.",
};

export const TutoringScheduleWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const evaluateRef = useRef<(() => Promise<{ allowed: boolean; reason?: string }>) | null>(null);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    if (evaluateRef.current) {
      const ruleResult = await evaluateRef.current();
      if (!ruleResult.allowed) {
        throw new Error(ruleResult.reason || 'Rule evaluation failed');
      }
    }

    const entityResult = await dataProvider.create('tutoring-schedules', {
      data: {
        tutor: { type: 'tutor', id: v.tutorId },
        agreement: { type: 'tutoringagreement', id: v.agreementId },
        status: 'Draft',
      },
    });

    const entityId = entityResult.data.id;
    const { finalState, transitions } = buildTransitionChain('tutoring-schedule', 'draft', ['PROPOSE']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'tutoring-schedule',
        entityId,
        entityName: `Schedule ${entityId.substring(0, 12)}`,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate } = useRuleEvaluation({
    ruleId: 'tutoring-schedule-guards',
    context: values as unknown as Record<string, unknown>,
  });
  useEffect(() => {
    evaluateRef.current = evaluate;
  }, [evaluate]);

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const vals = values as unknown as FormValues;

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'details':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Tutor ID" value={vals.tutorId} onChange={e => updateField('tutorId', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Agreement ID" value={vals.agreementId} onChange={e => updateField('agreementId', e.target.value)} fullWidth />
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review schedule details before creating. A workflow will be initialized in Draft state.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Schedule Info</Typography>
                <Typography variant="body2"><strong>Tutor:</strong> {vals.tutorId}</Typography>
                <Typography variant="body2"><strong>Agreement:</strong> {vals.agreementId}</Typography>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Tutoring Schedule Created"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Tutor', value: vals.tutorId },
            { label: 'Agreement', value: vals.agreementId },
          ]}
          footerActions={[
            { label: 'View Schedules', to: '/tutoring-schedules' },
            { label: 'Create Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
